import Card from 'components/card';
import InputField from 'components/fields/InputField';
import Checkbox from 'components/checkbox';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    MdContentCopy,
    MdOutlinePictureAsPdf,
    MdOutlineClose,
    MdExpandMore,
    MdExpandLess,
} from 'react-icons/md';
import { getFile, uploadCompanyLogo, deleteUserFile } from 'interfaces/user';
import { getAnnouncementPdfStats } from 'interfaces/customer';

const SHOP_URL = 'https://shop.toola.fr';
const ANNOUNCEMENT_PDF_NAME = 'announcement.pdf';

const CommandPageInfo = (props: { user: any; handleChange: any }) => {
    const { user, handleChange } = props;
    const nav = useNavigate();
    const shopLink = user?.shopSlug ? `${SHOP_URL}/c/${user.shopSlug}` : '';
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [pdfStats, setPdfStats] = useState<{
        total: number;
        viewedCount: number;
        customers: { id: string; company?: string; displayName?: string; mail?: string; viewedAt: string | null }[];
    } | null>(null);
    const [statsExpanded, setStatsExpanded] = useState(false);

    const copyShopLink = () => {
        if (!shopLink) return;
        navigator.clipboard.writeText(shopLink);
        toast.success('Lien copié !');
    };

    const loadPdfStats = async () => {
        const res = await getAnnouncementPdfStats();
        setPdfStats(res || null);
    };

    const loadPdfPreview = async () => {
        const res = await getFile(ANNOUNCEMENT_PDF_NAME);
        const url = res?.data?.url || null;
        setPdfUrl(url);
        if (url) loadPdfStats();
        else setPdfStats(null);
    };

    useEffect(() => {
        loadPdfPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePdfChange = async (file: File) => {
        if (file.type !== 'application/pdf') {
            toast.error('Le fichier doit être un PDF.');
            return;
        }
        setUploadingPdf(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', ANNOUNCEMENT_PDF_NAME);
        const res = await uploadCompanyLogo(formData);
        setUploadingPdf(false);
        if (res?.error) {
            toast.error("Erreur lors de l'envoi du PDF.");
            return;
        }
        toast.success('PDF ajouté !');
        loadPdfPreview();
    };

    const handlePdfDelete = async () => {
        setUploadingPdf(true);
        await deleteUserFile(ANNOUNCEMENT_PDF_NAME);
        setUploadingPdf(false);
        setPdfUrl(null);
        setPdfStats(null);
        toast.success('PDF supprimé.');
    };

    return (
        <Card extra={'w-full mt-3 px-6 py-6 border'}>
            {/* Header */}
            <div className="w-full px-1">
                <h4 className="text-xl font-medium">Informations générales</h4>
            </div>
            {/* inputs */}
            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="col-span-2 rounded-lg bg-blue-50 border border-blue-200 p-3">
                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                        Lien à mettre sur votre site internet
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                        Utilisez ce lien (et non le lien générique shop.toola.fr/command)
                        pour que les visiteurs de votre site soient bien rattachés à
                        votre boutique, notamment pour le bouton "Intéressé par le catalogue".
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                        <code className="flex-1 truncate rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs text-navy-700">
                            {shopLink || 'Génération du lien en cours...'}
                        </code>
                        <button
                            onClick={copyShopLink}
                            disabled={!shopLink}
                            className="flex items-center gap-1 rounded-lg bg-[#0260CB] px-3 py-2 text-xs font-medium text-white hover:bg-[#01499b] disabled:opacity-50"
                        >
                            <MdContentCopy size={14} />
                            Copier
                        </button>
                    </div>
                    <div className="mt-3">
                        <InputField
                            label="Personnaliser l'identifiant (optionnel)"
                            placeholder="votre-entreprise"
                            value={user?.shopSlug || ''}
                            id="shopSlug"
                            type="text"
                            onChange={(e: any) =>
                                handleChange({
                                    target: {
                                        id: 'shopSlug',
                                        value: e.target.value
                                            .toLowerCase()
                                            .replace(/[^a-z0-9-]+/g, '-'),
                                    },
                                })
                            }
                            inputSmall={true}
                        />
                    </div>
                </div>
                <div className="col-span-2">
                    <InputField
                        label="Informations de contact"
                        value={user?.contactInformation}
                        placeholder={
                            '+33 6 12 34 56 78 - contact@fournisseur.fr'
                        }
                        id="contactInformation"
                        type="text"
                        onChange={handleChange}
                        inputSmall={true}
                    />
                </div>
                <div className="col-span-2">
                    <InputField
                        label="Annonces (mise en avant via un pop-up)"
                        placeholder={'Aucune livraison possible le 25 décembre'}
                        value={user?.announcements}
                        id="announcements"
                        type="text"
                        onChange={handleChange}
                        inputSmall={true}
                    />
                </div>
                <div className="col-span-2 rounded-lg border border-gray-200 p-3">
                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                        Document PDF joint au pop-up (optionnel)
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                        Le client pourra le consulter directement dans le pop-up, sans
                        quitter la boutique (fonctionne aussi sur mobile).
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => {
                            if (e.target.files?.[0]) handlePdfChange(e.target.files[0]);
                            e.target.value = '';
                        }}
                    />
                    {pdfUrl ? (
                        <div className="mt-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <MdOutlinePictureAsPdf size={18} className="text-red-500 shrink-0" />
                            <a
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 truncate text-xs text-navy-700 hover:underline"
                            >
                                {ANNOUNCEMENT_PDF_NAME}
                            </a>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingPdf}
                                className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium hover:bg-gray-100 disabled:opacity-50"
                            >
                                Remplacer
                            </button>
                            <button
                                type="button"
                                onClick={handlePdfDelete}
                                disabled={uploadingPdf}
                                className="rounded-lg p-1 text-gray-500 hover:bg-gray-200 disabled:opacity-50"
                                title="Supprimer le PDF"
                            >
                                <MdOutlineClose size={16} />
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingPdf}
                            className="mt-2 flex items-center gap-1 rounded-lg bg-[#0260CB] px-3 py-2 text-xs font-medium text-white hover:bg-[#01499b] disabled:opacity-50"
                        >
                            <MdOutlinePictureAsPdf size={14} />
                            {uploadingPdf ? 'Envoi en cours...' : 'Ajouter un PDF'}
                        </button>
                    )}

                    {pdfUrl && pdfStats && (
                        <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <button
                                type="button"
                                onClick={() => setStatsExpanded((v) => !v)}
                                className="flex w-full items-center justify-between text-left"
                            >
                                <span className="text-xs font-medium text-navy-700">
                                    {pdfStats.viewedCount}/{pdfStats.total} client
                                    {pdfStats.total > 1 ? 's' : ''} ont vu le document
                                </span>
                                {statsExpanded ? (
                                    <MdExpandLess size={18} className="text-gray-500" />
                                ) : (
                                    <MdExpandMore size={18} className="text-gray-500" />
                                )}
                            </button>
                            {statsExpanded && (
                                <div className="mt-2 max-h-64 overflow-y-auto">
                                    {pdfStats.customers.length === 0 ? (
                                        <p className="text-xs text-gray-400">
                                            Aucun client pour le moment.
                                        </p>
                                    ) : (
                                        <table className="w-full text-xs">
                                            <tbody>
                                                {pdfStats.customers.map((c) => (
                                                    <tr key={c.id} className="border-t border-gray-200">
                                                        <td className="py-1.5 pr-2 truncate text-navy-700">
                                                            {c.company || c.displayName || c.mail}
                                                        </td>
                                                        <td className="py-1.5 text-right whitespace-nowrap">
                                                            {c.viewedAt ? (
                                                                <span className="text-green-600">
                                                                    Vu le{' '}
                                                                    {new Date(c.viewedAt).toLocaleDateString('fr-FR')}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400">Pas vu</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="col-span-2 flex items-center gap-2 rounded-lg bg-yellow-50 p-3 border border-yellow-200">
                    <Checkbox
                        checked={!!user?.showProductDetailsOnShop}
                        onChange={(e: any) =>
                            handleChange({
                                target: {
                                    id: 'showProductDetailsOnShop',
                                    value: e.target.checked,
                                },
                            })
                        }
                    />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                            Afficher les détails produit (variété, producteur, calibre, origine...) sur la boutique
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                            Désactivé par défaut. Activez pour afficher ce bloc sur vos fiches produit dès qu'un champ est renseigné.
                        </p>
                    </div>
                </div>
                <div className="col-span-2 flex items-center gap-2 rounded-lg bg-yellow-50 p-3 border border-yellow-200">
                    <Checkbox
                        checked={!!user?.restrictManualOrdersToShopVisibility}
                        onChange={(e: any) =>
                            handleChange({
                                target: {
                                    id: 'restrictManualOrdersToShopVisibility',
                                    value: e.target.checked,
                                },
                            })
                        }
                    />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                            Limiter les commandes manuelles aux produits visibles sur la boutique B2B
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                            Désactivé par défaut. Activez pour qu'un produit masqué de la boutique B2B n'apparaisse plus non plus dans la création de commande manuelle.
                        </p>
                    </div>
                </div>
                <div className="col-span-2 flex items-center gap-2 rounded-lg bg-yellow-50 p-3 border border-yellow-200">
                    <Checkbox
                        checked={!!user?.stockManagementEnabled}
                        onChange={(e: any) =>
                            handleChange({
                                target: {
                                    id: 'stockManagementEnabled',
                                    value: e.target.checked,
                                },
                            })
                        }
                    />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-navy-700 dark:text-white">
                            Gérer mon stock sur la boutique B2B
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                            Désactivé par défaut. Activez si vos quantités en stock sont fiables : la boutique affichera les niveaux de stock, limitera les quantités commandables au stock disponible, et vos clients pourront s'abonner aux alertes de retour en stock.
                        </p>
                    </div>
                </div>
                {/* Card and button to handle my product visible or not per default */}
                <div
                    className="mt-1 col-span-2 bg-toola-500/20 shadow-sm rounded-lg p-2 hover:border hover:cursor-pointer"
                    onClick={() => nav('/b2b-display-global')}
                >
                    <h4 className="text-sm font-medium">
                        Gérer la visibilité de mes produits
                    </h4>
                    <p className="mt-1 text-xs">
                        Vous pouvez choisir de cacher ou non vos produits par
                        défaut
                    </p>
                </div>
            </div>
        </Card>
    );
};

export default CommandPageInfo;
